import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, updateDoc, getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { initializeApp } from 'firebase/app';
import { getFirebaseConfig } from '../firebaseConfig';

// Initialize Firebase if not already initialized
const app = initializeApp(getFirebaseConfig());
const messaging = getMessaging(app);

// Your VAPID key from Firebase Console
const VAPID_KEY = 'BIrX_BBIo8_hQeNqxdvzRkgjWI2zuASRUBgRx8SvUjYJraCmYHLjzNts9oTrH2RR9vPVSwdCYFM_RfOd15l5luw';

let serviceWorkerRegistration = null;

// Check if the browser supports notifications
const isNotificationSupported = () => {
    return 'Notification' in window;
};

// Check if the browser supports service workers
const isServiceWorkerSupported = () => {
    return 'serviceWorker' in navigator;
};

// Get current notification permission
const getNotificationPermission = () => {
    return Notification.permission;
};

// Request notification permission with retry
export const requestNotificationPermission = async (retryCount = 0) => {
    try {
        // Check browser support
        if (!isNotificationSupported()) {
            throw new Error('This browser does not support notifications');
        }

        if (!isServiceWorkerSupported()) {
            throw new Error('This browser does not support service workers');
        }

        // Check current permission
        const currentPermission = getNotificationPermission();
        
        if (currentPermission === 'granted') {
            // Permission already granted, proceed with token generation
            return await generateFCMToken();
        }

        if (currentPermission === 'denied') {
            // If permission was previously denied, we need to guide the user
            throw new Error('Notification permission was previously denied. Please enable notifications in your browser settings.');
        }

        // Request permission
        const permission = await Notification.requestPermission();
        console.log('Notification permission status:', permission);

        if (permission === 'granted') {
            // Permission granted, proceed with token generation
            return await generateFCMToken();
        } else if (permission === 'default' && retryCount < 2) {
            // If permission is 'default' (user dismissed), retry once
            return await requestNotificationPermission(retryCount + 1);
        } else {
            throw new Error('Notification permission denied');
        }
    } catch (error) {
        console.error('Error in requestNotificationPermission:', error);
        throw error;
    }
};

// Generate FCM token
const generateFCMToken = async () => {
    try {
        // Check if service worker is already registered, otherwise register it
        if (!serviceWorkerRegistration) {
            // Try to get existing registration first
            const existingRegistration = await navigator.serviceWorker.getRegistration('/');
            if (existingRegistration) {
                serviceWorkerRegistration = existingRegistration;
                console.log('Using existing Service Worker registration:', serviceWorkerRegistration);
            } else {
                // Register service worker if not already registered
                serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                    scope: '/'
                });
                console.log('Service Worker registered:', serviceWorkerRegistration);
            }
        }

        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;

        // Get FCM token
        const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: serviceWorkerRegistration
        });

        if (token) {
            if (auth.currentUser) {
                // Store the FCM token in Firestore
                await updateDoc(doc(db, 'users', auth.currentUser.uid), {
                    fcmToken: token
                });
            }
            return token;
        }
        throw new Error('Failed to generate FCM token');
    } catch (error) {
        console.error('Error generating FCM token:', error);
        throw error;
    }
};

// Send test notification
export const sendTestNotification = async () => {
    try {
        if (!isNotificationSupported()) {
            throw new Error('This browser does not support notifications');
        }

        if (!serviceWorkerRegistration) {
            serviceWorkerRegistration = await navigator.serviceWorker.ready;
        }

        const permission = getNotificationPermission();
        if (permission !== 'granted') {
            throw new Error('Notification permission is required');
        }

        await serviceWorkerRegistration.showNotification('Test Notification', {
            body: 'This is a test notification',
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: 'test-notification',
            requireInteraction: true
        });
    } catch (error) {
        console.error('Error sending test notification:', error);
        throw error;
    }
};

// Listen for incoming messages
export const onMessageListener = () => {
    return new Promise((resolve) => {
        onMessage(messaging, (payload) => {
            console.log('Message received:', payload);
            resolve(payload);
        });
    });
}; 